import { Box, Card, CardContent, Button, Typography, Skeleton, useMediaQuery, Theme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { EXAM_RESULTS_LOGO_URL } from "../../../../utils/constants";
import { ExamHistory } from "../../../../api/interfaces/Exam";
import { SkeletonDashboardRecentExams } from "../../../../components/skeleton/SkeletonDashboardRecentExams/SkeletonDashboardRecentExams";
import PinnacleDrawer from "../../../../components/PinnacleDrawer/PinnacleDrawer";
import { useState } from "react";
import ExamAttemptDetailsDrawer from "./drawer/ExamAttemptDetailsDrawer";

interface RecentExamsProps {
    recentExams?: ExamHistory[];
    isLoading: boolean;
}
export const RecentExams: React.FC<RecentExamsProps> = ({ recentExams = [], isLoading }) => {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

    const hasExams = recentExams.length > 0;
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [examAttemptInfo, setExamAttemptInfo] = useState<ExamHistory | null>(null);

    const handleViewExamAttemptDetails = (exam: ExamHistory) => {
        setExamAttemptInfo(exam);
        setIsDrawerOpen(true);
    }

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setExamAttemptInfo(null);
    }

    return (
        <>
            <Card id="recent-exams-section" sx={{ borderRadius: '8px', '--Paper-shadow': 'none !important' }}>
                <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 3,
                        }}
                    >
                        <Typography variant="h3" fontWeight={600}>
                            Recent Exams
                        </Typography>

                        <Box
                            component="img"
                            src={EXAM_RESULTS_LOGO_URL}
                            alt="Recent Exams Logo"
                            sx={{
                                width: 40,
                                height: 40,
                                objectFit: "contain",
                            }}
                        />
                    </Box>

                    {isLoading ? (
                        <SkeletonDashboardRecentExams />
                    ) : hasExams ? (
                        <>
                            {/* TABLE WRAPPER FOR MOBILE */}
                            <Box
                                sx={{
                                    overflowX: "auto",
                                    width: "100%",
                                    pb: 1,
                                    "&::-webkit-scrollbar": {
                                        height: "6px",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#d1c4e9",
                                        borderRadius: "4px",
                                    },
                                }}
                            >
                                {/* Table Container */}
                                <Box sx={{ minWidth: isMobile ? "600px" : "100%" }}>

                                    {/* Header */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "2fr 1fr 1fr 1.2fr",
                                            fontWeight: 600,
                                            color: "#5840BA",
                                            fontSize: "14px",
                                            mb: 1,
                                            px: 1,
                                            position: "sticky",
                                            top: 0,
                                            backgroundColor: "#FFFFFF",
                                            zIndex: 2,
                                            borderBottom: "1px solid #EFEFEF",
                                        }}
                                    >
                                        <Typography variant="body2">Exam Name</Typography>
                                        <Typography variant="body2">Exam Code</Typography>
                                        <Typography variant="body2">Score</Typography>
                                        <Typography variant="body2" sx={{ textAlign: "center" }}>
                                            Action
                                        </Typography>
                                    </Box>

                                    {/* Scrollable Vertical List */}
                                    <Box
                                        sx={{
                                            maxHeight: "220px",
                                            overflowY: "auto",
                                            "&::-webkit-scrollbar": {
                                                width: "6px",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#d1c4e9",
                                                borderRadius: "4px",
                                            },
                                        }}
                                    >
                                        {recentExams.map((exam, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "2fr 1fr 1fr 1.2fr",
                                                    alignItems: "center",
                                                    py: 1.5,
                                                    px: 1,
                                                    backgroundColor: i % 2 === 0 ? "#F7F8FD" : "#FFFFFF",
                                                }}
                                            >
                                                <Typography sx={{ fontSize: "12px", color: "#373737" }}>
                                                    {exam.examName}
                                                </Typography>

                                                <Typography sx={{ fontSize: "12px", color: "#373737" }}>
                                                    {exam.examCode}
                                                </Typography>

                                                <Typography sx={{ fontSize: "12px", color: "#373737" }}>
                                                    {exam.score === null ? "TBA" : exam.score} / {exam.numberOfQuestions}
                                                </Typography>

                                                <Box sx={{ textAlign: "center" }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        endIcon={<VisibilityOutlinedIcon sx={{ fontSize: "12px" }} />}
                                                        sx={{
                                                            borderRadius: "4px",
                                                            borderColor: "#5840BA",
                                                            color: "#5840BA",
                                                            textTransform: "none",
                                                            fontSize: "12px",
                                                            minWidth: "85px",
                                                            "&:hover": {
                                                                backgroundColor: "#F7F5FF",
                                                                borderColor: "#5840BA",
                                                            },
                                                        }}
                                                        onClick={() => handleViewExamAttemptDetails(exam)}
                                                    >
                                                        Details
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 5,
                            }}
                        >
                            {/* <Box
                                component="img"
                                src={EMPTY_STATE_ILLUSTRATION_URL}
                                alt="No Exams Illustration"
                                sx={{
                                    width: 200,
                                    height: 200,
                                    mb: 2,
                                    opacity: 0.85,
                                }}
                            /> */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#818181",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    mt: 0.5,
                                }}
                            >
                                No data to display
                            </Typography>
                        </Box>
                    )
                    }
                </CardContent >
            </Card >
            <PinnacleDrawer
                title={examAttemptInfo?.examName ?? ''}
                isDrawerOpen={isDrawerOpen}
                onClose={() => handleDrawerClose()}
            >
                <ExamAttemptDetailsDrawer
                    examCodeName={examAttemptInfo?.examCode ?? ''}
                    examAttemptId={examAttemptInfo?._id ?? ''}
                />
            </PinnacleDrawer>
        </>
    );
};
